import { Form, Head } from '@inertiajs/react'
import Heading from '@/components/heading'
import InputError from '@/components/input-error'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AppLayout from '@/layouts/app-layout'
import { serversPath, serverPath, serverAppsPath, newServerAppPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { Server } from '@/types'

type AppsNewProps = {
  server: Pick<Server, 'id' | 'name'>
}

export default function AppsNew({ server }: AppsNewProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
    { title: 'Apps', href: serverAppsPath(server.id) },
    { title: 'New App', href: newServerAppPath(server.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New App" />

      <div className="p-6">
        <Heading
        title="Add Application"
        description={`Create a new app on ${server.name}`}
      />

      <Card className="mt-6 max-w-2xl">
        <CardHeader>
          <CardTitle>Application Details</CardTitle>
          <CardDescription>
            Enter the basic information about your application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form
            method="post"
            action={serverAppsPath(server.id)}
            className="space-y-6"
          >
            {({ processing, errors }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="app[name]"
                    type="text"
                    required
                    autoFocus
                    placeholder="My Application"
                  />
                  <InputError messages={errors['app.name']} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="app[location]"
                    type="text"
                    placeholder="/var/www/myapp"
                  />
                  <p className="text-xs text-muted-foreground">
                    The local path to the application on the server
                  </p>
                  <InputError messages={errors['app.location']} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="app_url">Primary URL</Label>
                  <Input
                    id="app_url"
                    name="app[app_url]"
                    type="url"
                    placeholder="https://myapp.example.com"
                  />
                  <InputError messages={errors['app.app_url']} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="other_urls">Other URLs</Label>
                  <Input
                    id="other_urls"
                    name="app[other_urls]"
                    type="text"
                    placeholder="https://staging.example.com, https://api.example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated list of additional URLs
                  </p>
                  <InputError messages={errors['app.other_urls']} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="health_urls">Health Check URLs</Label>
                  <Input
                    id="health_urls"
                    name="app[health_urls]"
                    type="text"
                    placeholder="https://myapp.example.com/health"
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated URLs to monitor for health
                  </p>
                  <InputError messages={errors['app.health_urls']} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="db_type">Database Type</Label>
                    <select
                      id="db_type"
                      name="app[db_type]"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">None</option>
                      <option value="postgresql">PostgreSQL</option>
                      <option value="mysql">MySQL</option>
                      <option value="sqlite">SQLite</option>
                    </select>
                    <InputError messages={errors['app.db_type']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="db_name">Database Name</Label>
                    <Input
                      id="db_name"
                      name="app[db_name]"
                      type="text"
                      placeholder="myapp_production"
                    />
                    <InputError messages={errors['app.db_name']} />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="notes">Notes (Markdown)</Label>
                  <textarea
                    id="notes"
                    name="app[notes]"
                    rows={5}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Application notes and documentation..."
                  />
                  <InputError messages={errors['app.notes']} />
                </div>

                <div className="flex justify-end gap-4">
                  <Button type="button" variant="outline" onClick={() => history.back()}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing && <Spinner />}
                    Create App
                  </Button>
                </div>
              </>
            )}
          </Form>
        </CardContent>
      </Card>
      </div>
    </AppLayout>
  )
}
